import { updateMealRecipe, PlannedMeal } from '../redux/mealPlanSlice';
import { patchRecipeIndexEntry } from '../redux/recipeIndexSlice';
import type { TranslatedMealUpdate } from '../services/mealPlanTranslation';

/**
 * Build the actions a batch of translation updates should produce.
 *
 * Its own module, deliberately: `useMealPlanTranslation` pulls in
 * `languageSlice` → `i18n` → `expo-localization`, which cannot be imported in
 * a plain jest environment. Keeping this dependency-light makes the contract
 * directly testable, and the repo has no hook-testing library.
 *
 * Each translated recipe writes to two places:
 *  - `mealPlan`, so the grocery list (which reads `extendedIngredients`) ends
 *    up in a single language;
 *  - `recipeIndex`, because that is where the planner grids read titles from.
 *    `backfillIndexFromMeals` only fills entries that are *missing*, so an
 *    already-indexed recipe would otherwise keep its original title forever —
 *    the translation would sit in Redux and never render.
 */
export function buildTranslationActions(
  meals: Pick<PlannedMeal, 'recipeId' | 'recipe'>[],
  updates: TranslatedMealUpdate[],
): unknown[] {
  const actions: unknown[] = [];

  for (const update of updates) {
    const meal = meals.find(m => m.recipeId === update.recipeId);
    if (!meal?.recipe) continue;

    // Prefer the full translation (ingredients + instructions) over the
    // title-only fallback.
    actions.push(updateMealRecipe({
      recipeId: update.recipeId,
      recipe: update.translatedRecipe ?? {
        ...meal.recipe,
        title: update.translatedTitle,
        isTranslated: true,
        translatedFrom: meal.recipe.originalLanguage === 'fr' ? 'fr' : 'en',
      },
    }));

    actions.push(patchRecipeIndexEntry({
      id: update.recipeId,
      changes: { title: update.translatedRecipe?.title ?? update.translatedTitle },
    }));
  }

  return actions;
}
